import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useState,
	Suspense,
	memo,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { cloneDeep, isNil, isUndefined } from "lodash";

import { Order, Executor, PhoneBlacklist } from "../../../../services";
import ChatMessage, { Priority } from "../../../../services/ChatMessage";
import { useTypedDispatch, useTypedSelector } from "../../../../redux/store";
import orderPage from "../../../../redux/reducers/OrdersPage";
import {
	openOrderCard,
	initOrderCard,
	closeOrderCard,
} from "../../../../redux/services/Order/card";
import { ordersAction as orders } from "../../../../redux/reducers/Orders";
import useModelSubscribe from "../../../../hooks/useModelSubscribe2";
import OrderStatus from "../../../../types/OrderStatus";
import ContextMenu from "../../../../components/OrderPageWidgets/Chat/components/ChatList/components/ContextMenu";
import { DeleteModal, OrderPageModelsLoader } from "../../../../components";
import { PerfProfiler } from "../../../../tests";
import { SOSModal } from "../../../../components/Orders";
import { StyledGrid, SuspenseLoader } from "../../../../components/common";
import { useNotification } from "../../../../components/NotificationProvider";
import { hasAccess } from "../../../../access";

import HeaderOrder from "./components/OrderHeader/OrderHeaderPage";
import MainOrderPage from "./components/MainOrderPage";
import OrderModal from "./components/OrderModal";
import SetOrderExecutorModal, {
	SetOrderExecutorModalMemo,
} from "./components/SetOrderExecutorModal";
import CloseOrderModal from "./components/CloseOrderModal";
import CopyOrderModal from "./components/CopyOrderModal";
import ClientCancelOrderModal from "./components/ClientCancelOrderModal";
import { CloseReason } from "./components/CloseOrderModal/components/Content";
import AddClientToBlacklistModal from "./components/AddClientToBlacklistModal";
import {
	TabKeys,
	TabAccessNames,
} from "./components/OrderHeader/components/FirstRow/constants/access";

const StyledMainPage = styled.div`
	flex-grow: 1;
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: stretch;
`;

const OrdersPage = () => {
	const dispatch = useTypedDispatch();
	const { t } = useTranslation();

	const { connected, authorized } = useTypedSelector((state) => state.prpc);
	const { oldId: LastIDReadInMessageSMS } = useTypedSelector(
		(state) => state.orders.chats.chat.lastRead,
	);
	const { error } = useNotification();

	const translateTexts = useMemo(
		() => ({
			error: t("notification.title.error") ?? "Error",
			orderNumber:
				t("utils.getColumnsModeOne.orderNumber") || "Order number",
			dataNotSave: t("notification.description.dataNotSave") ?? "Error",
		}),
		[t],
	);

	const lang = useTypedSelector((state) => state.session.language);
	const orderType = useTypedSelector(
		(state) => state.ordersPageReducer.ordersType,
	);
	const isCallUs = useTypedSelector(
		(state) => state.softphoneWidget.isCallUs,
	);
	const orderSettings = useTypedSelector((state) => state.settings.order);
	const existOrder = useTypedSelector(
		(state) => state.ordersPageReducer.activeOrder,
	);

	const modal = useTypedSelector((state) => state.ordersPageReducer.modal);
	const { booferAnswerParentID, contextMenuCoordinate, isShowContextMenu } =
		useTypedSelector((state) => state.orders.chats.chat);
	const personalRole = useTypedSelector(
		(state) => state.account.personalRole,
	);

	const activeOrder = useMemo(() => existOrder, [existOrder]);
	const orderNumber = useMemo(
		() => activeOrder?.orderNumber || "",
		[activeOrder?.orderNumber],
	);

	const canSubscribe: boolean = useMemo(
		() => connected && authorized,
		[connected, authorized],
	);

	const subscribeChatMessage = useModelSubscribe(
		{
			lang,
			priority: Priority.SOS,
			unreadMessages: true,
		},
		ChatMessage,
	);

	const [closeOrderModalValue, setCloseOrderModalValue] =
		useState<CloseOrderModal.Value | null>(null);

	const [isLoaderTaximeter, setLoaderTaximeter] = useState<boolean>(false);
	const [isWarnBeforeCloseModalOpen, setIsWarnBeforeCloseModalOpen] =
		useState(false);
	const [
		isWarnBeforeCloseOrderWithTaximeterModalOpen,
		setIsWarnBeforeOrderWithTaximeterModalOpen,
	] = useState<boolean>(false);
	const [paramsOrderForClosed, setParamsOrderForClosed] =
		useState<Order.CloseOptions | null>(null);
	const [idOrderForClosed, setIdOrderForClosed] = useState<number | null>(
		null,
	);

	const [blackListForClosed, setBlackListForClosed] =
		useState<AddClientToBlacklistModal.Value | null>(null);

	const [isCloseOrderModalOpen, setIsCloseOrderModalOpen] = useState(false);
	const [isClientCancelOrderModalOpen, setIsClientCancelOrderModalOpen] =
		useState(false);
	const [
		isAddClientToBlackListModalOpen,
		setIsAddClientToBlackListModalOpen,
	] = useState(false);
	const [isCloneOrderModalOpen, setIsCloneOrderModalOpen] = useState(false);
	const [isSetOrderExecutorModalOpen, setIsSetOrderExecutorModalOpen] =
		useState(false);
	const [isRefuseOrderModalOpen, setIsRefuseOrderModalOpen] = useState(false);

	const [activePoint, setActivePoint] = useState(-1);
	const [pointModal, setPointModal] = useState({
		visible: false,
		isEdit: false,
		isInsert: false,
	});
	const [showSOSModal, setShowSOSModal] = useState<boolean>(false);

	const [warnBeforeCloseOrderType, setWarnBeforeCloseOrderType] = useState<
		number[]
	>([]);

	const closeCards = React.useCallback(
		(ids: any[] = []) => {
			dispatch(closeOrderCard(...ids));
		},
		[dispatch],
	);

	const blockClient = useCallback(
		(value: AddClientToBlacklistModal.Value) => {
			const { customer } = activeOrder?.passengers?.[0] || {};
			const [customerPhone] = activeOrder.phones || [];

			PhoneBlacklist.store({
				level: value.level,
				description: value.comment ?? "",
				phone: customerPhone?.number,
				firstName: customer?.person?.name || " ",
				lastName: customer?.person?.surname || " ",
				fatherName: customer?.person?.fatherName || " ",
				companyIds: [activeOrder?.taxiService?.company?.id],
			});
		},
		[
			activeOrder?.passengers,
			activeOrder?.phones,
			activeOrder?.taxiService?.company?.id,
		],
	);

	const closeContextMenu = useCallback(() => {
		dispatch(orders.chats.chat.setIsShowContextMenu(false));
	}, [dispatch]);

	const addHideOrderId = useCallback(
		(id: number) => {
			dispatch(orderPage.actions.addHideOrderId({ id }));
		},
		[dispatch],
	);

	const removeHideOrderId = useCallback(
		(id: number) => {
			dispatch(orderPage.actions.removeHideOrderId({ id }));
		},
		[dispatch],
	);

	const openCloseOrderModal = useCallback(() => {
		if (!activeOrder.id) return;
		let isOpen = false;
		const warnType: number[] = [];

		if (
			orderSettings.general.warnWhenClosingNoDistanceOrder &&
			activeOrder?.points?.length === 1
		) {
			isOpen = true;
			warnType.push(1);
		}

		if (
			(orderSettings.general.warnWhenClosingMeteredOrder &&
				activeOrder?.additionalFields?.processedByTaximeter) ||
			(orderSettings.general.warnWhenClosingMeteredOrder &&
				activeOrder?.additionalFields?.taximeter)
		) {
			isOpen = true;
			warnType.push(2);
		}

		if (orderSettings.general.warnWhenClosingZeroCostOrder) {
			const orderCost: number = activeOrder?.cost?.value ?? 0;

			if (orderCost <= 0) {
				isOpen = true;
				warnType.push(3);
			}
		}

		if (
			orderSettings.general.warnWhenClosingOwnOrder &&
			activeOrder.source === "executor"
		) {
			isOpen = true;
			warnType.push(4);
		}

		if (isOpen) {
			setWarnBeforeCloseOrderType(warnType);
			setIsWarnBeforeCloseModalOpen(true);
			return;
		}
		setWarnBeforeCloseOrderType([]);
		setIsCloseOrderModalOpen(true);
	}, [
		activeOrder?.additionalFields?.processedByTaximeter,
		activeOrder?.additionalFields?.taximeter,
		activeOrder?.cost?.value,
		activeOrder.id,
		activeOrder?.points?.length,
		activeOrder.source,
		orderSettings.general.warnWhenClosingMeteredOrder,
		orderSettings.general.warnWhenClosingNoDistanceOrder,
		orderSettings.general.warnWhenClosingOwnOrder,
		orderSettings.general.warnWhenClosingZeroCostOrder,
	]);

	const submitCloseOrderModal = useCallback(
		async (value: CloseOrderModal.Value) => {
			setIsCloseOrderModalOpen(false);

			if (!activeOrder.id) return;

			if (
				value.reason === CloseReason.ClientCanceled ||
				value.addClientToBlackList
			) {
				setCloseOrderModalValue(value);

				if (value.reason === CloseReason.ClientCanceled) {
					setIsClientCancelOrderModalOpen(true);
				} else {
					setIsAddClientToBlackListModalOpen(true);
				}
			} else {
				if (activeOrder?.additionalFields?.processedByTaximeter) {
					setLoaderTaximeter(true);
					const res = await Order.close(activeOrder.id, {
						status: value.reason,
						processedByTaximeter:
							activeOrder?.additionalFields?.processedByTaximeter,
						timeoutForTaximeter:
							orderSettings?.general?.taxiOrderCloseUpdateWaitTime
								?.value,
					});
					if (!res?.id)
						error(
							`${translateTexts.error} ${translateTexts.orderNumber}: ${orderNumber}`,

							res?.message ||
								res?.error?.message ||
								res?.data?.message
								? `${translateTexts.dataNotSave}: ${
										res?.message ||
										res?.error?.message ||
										res?.data?.message
								  }`
								: translateTexts.dataNotSave,
							true,
						);

					if (!res?.id) {
						setParamsOrderForClosed({ status: value.reason });
						setIdOrderForClosed(activeOrder.id);
						setIsWarnBeforeOrderWithTaximeterModalOpen(true);
					}
					setLoaderTaximeter(false);
					return;
				}

				addHideOrderId(activeOrder.id);
				const res = await Order.close(activeOrder.id, {
					status: value.reason,
				});

				if (!res?.id)
					error(
						`${translateTexts.error} ${translateTexts.orderNumber}: ${orderNumber}`,
						res?.message ||
							res?.error?.message ||
							res?.data?.message
							? `${translateTexts.dataNotSave}: ${
									res?.message ||
									res?.error?.message ||
									res?.data?.message
							  }`
							: translateTexts.dataNotSave,
						true,
					);

				removeHideOrderId(activeOrder.id);
			}
		},
		[
			activeOrder.id,
			activeOrder?.additionalFields?.processedByTaximeter,
			addHideOrderId,
			error,
			translateTexts.error,
			translateTexts.orderNumber,
			translateTexts.dataNotSave,
			orderNumber,
			removeHideOrderId,
			orderSettings?.general?.taxiOrderCloseUpdateWaitTime?.value,
		],
	);

	const closeCloseOrderModal = useCallback(() => {
		setIsCloseOrderModalOpen(false);
	}, []);

	const closeClientCancelOrderModal = useCallback(() => {
		setIsClientCancelOrderModalOpen(false);
		setCloseOrderModalValue(null);
	}, []);

	const submitClientCancelOrderModal = useCallback(
		async (value: ClientCancelOrderModal.Value) => {
			if (
				closeOrderModalValue?.addClientToBlackList &&
				!value.blockComment
			) {
				return;
			}

			setIsClientCancelOrderModalOpen(false);
			setCloseOrderModalValue(null);

			if (activeOrder?.additionalFields?.processedByTaximeter) {
				setLoaderTaximeter(true);

				const res = await Order.close(activeOrder.id, {
					status: closeOrderModalValue?.reason,
					subStatus: value.reason,
					comment: value.comment,
					processedByTaximeter:
						activeOrder?.additionalFields?.processedByTaximeter,
					timeoutForTaximeter:
						orderSettings?.general?.taxiOrderCloseUpdateWaitTime
							?.value,
				});

				if (!res?.id)
					error(
						`${translateTexts.error} ${translateTexts.orderNumber}: ${orderNumber}`,
						res?.message ||
							res?.error?.message ||
							res?.data?.message
							? `${translateTexts.dataNotSave}: ${
									res?.message ||
									res?.error?.message ||
									res?.data?.message
							  }`
							: translateTexts.dataNotSave,
						true,
					);
				if (!res.id) {
					setParamsOrderForClosed({
						status: closeOrderModalValue?.reason,
						subStatus: value.reason,
						comment: value.comment,
					});
					setIdOrderForClosed(activeOrder.id);
					if (closeOrderModalValue?.addClientToBlackList) {
						setBlackListForClosed({
							level: value.blockLevel,
							comment: value.blockComment,
						});
					}

					setIsWarnBeforeOrderWithTaximeterModalOpen(true);
				}
				setLoaderTaximeter(false);
				return;
			}

			addHideOrderId(activeOrder.id);
			const res = await Order.close(activeOrder.id, {
				status: closeOrderModalValue?.reason,
				subStatus: value.reason,
				comment: value.comment,
			});
			if (!res?.id)
				error(
					`${translateTexts.error} ${translateTexts.orderNumber}: ${orderNumber}`,
					res?.message || res?.error?.message || res?.data?.message
						? `${translateTexts.dataNotSave}: ${
								res?.message ||
								res?.error?.message ||
								res?.data?.message
						  }`
						: translateTexts.dataNotSave,
					true,
				);

			removeHideOrderId(activeOrder.id);

			if (closeOrderModalValue?.addClientToBlackList) {
				blockClient({
					level: value.blockLevel,
					comment: value.blockComment,
				});
			}
		},
		[
			closeOrderModalValue?.addClientToBlackList,
			closeOrderModalValue?.reason,
			activeOrder?.additionalFields?.processedByTaximeter,
			activeOrder.id,
			addHideOrderId,
			error,
			translateTexts.error,
			translateTexts.orderNumber,
			translateTexts.dataNotSave,
			orderNumber,
			removeHideOrderId,
			orderSettings?.general?.taxiOrderCloseUpdateWaitTime?.value,
			blockClient,
		],
	);

	const closeAddClientToBlacklistModal = useCallback(() => {
		setIsAddClientToBlackListModalOpen(false);
		setCloseOrderModalValue(null);
	}, []);

	const submitAddClientToBlacklistModal = useCallback(
		async (value: AddClientToBlacklistModal.Value) => {
			if (!value.comment) return;

			setIsAddClientToBlackListModalOpen(false);
			setCloseOrderModalValue(null);

			if (activeOrder?.additionalFields?.processedByTaximeter) {
				setLoaderTaximeter(true);
				const res = await Order.close(activeOrder.id, {
					status: closeOrderModalValue?.reason,
					processedByTaximeter:
						activeOrder?.additionalFields?.processedByTaximeter,
					timeoutForTaximeter:
						orderSettings?.general?.taxiOrderCloseUpdateWaitTime
							?.value,
				});
				if (!res?.id)
					error(
						`${translateTexts.error} ${translateTexts.orderNumber}: ${orderNumber}`,
						res?.message ||
							res?.error?.message ||
							res?.data?.message
							? `${translateTexts.dataNotSave}: ${
									res?.message ||
									res?.error?.message ||
									res?.data?.message
							  }`
							: translateTexts.dataNotSave,
						true,
					);

				if (!res?.id) {
					setParamsOrderForClosed({
						status: closeOrderModalValue?.reason,
					});
					setIdOrderForClosed(activeOrder.id);
					setBlackListForClosed(value);

					setIsWarnBeforeOrderWithTaximeterModalOpen(true);
				}
				setLoaderTaximeter(false);
				return;
			}
			addHideOrderId(activeOrder.id);

			const res = await Order.close(activeOrder.id, {
				status: closeOrderModalValue?.reason,
			});
			if (!res?.id)
				error(
					`${translateTexts.error} ${translateTexts.orderNumber}: ${orderNumber}`,
					res?.message || res?.error?.message || res?.data?.message
						? `${translateTexts.dataNotSave}: ${
								res?.message ||
								res?.error?.message ||
								res?.data?.message
						  }`
						: translateTexts.dataNotSave,
					true,
				);

			removeHideOrderId(activeOrder.id);

			blockClient(value);
		},
		[
			activeOrder?.additionalFields?.processedByTaximeter,
			activeOrder.id,
			addHideOrderId,
			closeOrderModalValue?.reason,
			error,
			translateTexts.error,
			translateTexts.orderNumber,
			translateTexts.dataNotSave,
			orderNumber,
			removeHideOrderId,
			blockClient,
			orderSettings?.general?.taxiOrderCloseUpdateWaitTime?.value,
		],
	);

	const cloneOrderModal = useCallback(
		async (data: {
			orderId: number | null;
			params: Order.CloseOptions | null;
			blackList: AddClientToBlacklistModal.Value | null;
		}) => {
			if (!data.orderId) return;
			if (!data.params) return;

			addHideOrderId(data.orderId);
			const res = Order.close(data.orderId, data.params);
			setParamsOrderForClosed(null);
			setIdOrderForClosed(null);
			const value = await res;
			if (!value?.id)
				error(
					`${translateTexts.error} ${translateTexts.orderNumber}: ${orderNumber}`,
					value?.message ||
						value?.error?.message ||
						value?.data?.message
						? `${translateTexts.dataNotSave}: ${
								value?.message ||
								value?.error?.message ||
								value?.data?.message
						  }`
						: translateTexts.dataNotSave,
					true,
				);

			removeHideOrderId(data.orderId);
			if (data.blackList) {
				blockClient(data.blackList);
				setBlackListForClosed(null);
			}

			setIsWarnBeforeOrderWithTaximeterModalOpen(false);
		},
		[
			addHideOrderId,
			error,
			translateTexts.error,
			translateTexts.orderNumber,
			translateTexts.dataNotSave,
			orderNumber,
			removeHideOrderId,
			blockClient,
		],
	);

	const openCloneOrderModal = useCallback(() => {
		setIsCloneOrderModalOpen(true);
	}, []);

	const closeCloneOrderModal = useCallback(() => {
		setIsCloneOrderModalOpen(false);
	}, []);

	const cloneOrderModalOnSubmit = useCallback(
		async (value: CopyOrderModal.Value) => {
			if (activeOrder.id) Order.copy(activeOrder.id, value);

			setIsCloneOrderModalOpen(false);
		},
		[activeOrder.id],
	);

	const openSetOrderExecutorModal = useCallback(() => {
		setIsSetOrderExecutorModalOpen(true);
	}, []);

	const closeSetOrderExecutorModal = useCallback(() => {
		setIsSetOrderExecutorModalOpen(false);
	}, []);

	const setOrderExecutorModalOnSubmit = useCallback(
		async (value: SetOrderExecutorModal.Value) => {
			if (isUndefined(value.executor)) return;
			const executorId = value.executor.id;
			const orderId = activeOrder.id;
			if (!value.executor.isWorking && value.openShift) {
				setIsSetOrderExecutorModalOpen(false);

				Executor.updateWorkingStatus(
					{
						id: executorId,
						workingStatus: true,
					},
					value.carId ?? null,
				).then(async (data) => {
					if (data?.error) {
						error(
							`${translateTexts.error} ${translateTexts.orderNumber}: ${orderNumber}`,
							data?.message ||
								data?.error?.message ||
								data?.data?.message
								? `${translateTexts.dataNotSave}: ${
										data?.message ||
										data?.error?.message ||
										data?.data?.message
								  }`
								: translateTexts.dataNotSave,
							true,
						);
						setIsSetOrderExecutorModalOpen(false);
						return;
					}
					if (orderId) {
						const time = value.arrivalTime * 60_000;
						const arrivalTime = Date.now() + time;

						const result = Order.assign(orderId, {
							executorId,
							arrivalTime,
						});

						const res = await result;

						setIsSetOrderExecutorModalOpen(false);
						if (res?.error) {
							error(
								`${translateTexts.error} ${translateTexts.orderNumber}: ${orderNumber}`,
								res?.message ||
									res?.error?.message ||
									res?.data?.message
									? `${translateTexts.dataNotSave}: ${
											res?.message ||
											res?.error?.message ||
											res?.data?.message
									  }`
									: translateTexts.dataNotSave,
								true,
							);
						}
					} else {
						setIsSetOrderExecutorModalOpen(false);
					}
				});
				setIsSetOrderExecutorModalOpen(false);
			}

			if (orderId && value.executor.isWorking) {
				const time = value.arrivalTime * 60_000;
				const arrivalTime = Date.now() + time;

				const result = Order.assign(orderId, {
					executorId: value.executor.id,
					arrivalTime,
				});
				setIsSetOrderExecutorModalOpen(false);
				const res = await result;
				if (res?.error) {
					error(
						`${translateTexts.error} ${translateTexts.orderNumber}: ${orderNumber}`,
						res?.message ||
							res?.error?.message ||
							res?.data?.message
							? `${translateTexts.dataNotSave}: ${
									res?.message ||
									res?.error?.message ||
									res?.data?.message
							  }`
							: translateTexts.dataNotSave,
						true,
					);
				}
			} else {
				setIsSetOrderExecutorModalOpen(false);
			}
		},
		[
			activeOrder.id,
			error,
			translateTexts.error,
			translateTexts.orderNumber,
			translateTexts.dataNotSave,
			orderNumber,
		],
	);

	const approveOrder = useCallback(() => {
		if (isUndefined(activeOrder.id) || isNil(activeOrder.offer?.executor))
			return;

		Order.accept(activeOrder.id);
	}, [activeOrder]);

	const recalculateOrder = useCallback(() => {
		if (isUndefined(activeOrder.id) || orderType === "closed") return;

		Order.recalculate(activeOrder.id);
	}, [activeOrder.id, orderType]);

	const recalculateOrderWithActualRates = useCallback(() => {
		if (isUndefined(activeOrder.id) || orderType === "closed") return;

		Order.recalculate(activeOrder.id, { useActualRates: true });
	}, [activeOrder.id, orderType]);

	const refuseOrder = useCallback(() => {
		if (isUndefined(activeOrder.id)) return;

		setIsRefuseOrderModalOpen(true);
	}, [activeOrder]);

	const closeRefuseOrderModal = useCallback(() => {
		setIsRefuseOrderModalOpen(false);
	}, []);

	const confirmRefuseOrderModal = useCallback(() => {
		setIsRefuseOrderModalOpen(false);

		if (isUndefined(activeOrder.id)) return;

		Order.refuse(activeOrder.id);
	}, [activeOrder.id]);

	const sendOrderToAir = useCallback(() => {
		if (isUndefined(activeOrder.id)) return;

		Order.startBroadcasting(activeOrder.id);
	}, [activeOrder.id]);

	const sendOrderToExecuting = useCallback(() => {
		if (isUndefined(activeOrder.id)) return;

		Order.sendOrderToExecuting(activeOrder.id);
	}, [activeOrder.id]);

	const toggleOrderAir = useCallback(() => {
		if (isUndefined(activeOrder.id)) return;

		if (activeOrder.status === OrderStatus.FREE_WAVE)
			Order.stopBroadcasting(activeOrder.id);
		else Order.startBroadcasting(activeOrder.id);
	}, [activeOrder.id, activeOrder.status]);

	const toggleShowClientPhoneToExecutor = useCallback(() => {
		if (isUndefined(activeOrder.id)) return;

		const additionalFields = cloneDeep(activeOrder.additionalFields);

		additionalFields.displaySettings =
			additionalFields.displaySettings ?? {};
		additionalFields.displaySettings.showCustomerPhone =
			!additionalFields.displaySettings.showCustomerPhone;

		Order.update({ id: activeOrder.id, additionalFields });
	}, [activeOrder]);

	const setCallToClientBeingRequired = useCallback(() => {
		if (isUndefined(activeOrder.id)) return;

		Order.updateCallStatus(activeOrder.id, {
			type: "customer",
			status: "need_call",
		});
	}, [activeOrder]);

	const setCallToClientSuccess = useCallback(() => {
		if (isUndefined(activeOrder.id)) return;

		Order.updateCallStatus(activeOrder.id, {
			type: "customer",
			status: "called",
		});
	}, [activeOrder]);

	const setCallToExecutorBeingRequired = useCallback(() => {
		if (isUndefined(activeOrder.id)) return;

		Order.updateCallStatus(activeOrder.id, {
			type: "executor",
			status: "need_call",
		});
	}, [activeOrder]);

	const setCallToExecutorSuccess = useCallback(() => {
		if (isUndefined(activeOrder.id)) return;

		Order.updateCallStatus(activeOrder.id, {
			type: "executor",
			status: "called",
		});
	}, [activeOrder]);

	const onRevertOrder = useCallback(() => {
		if (isUndefined(activeOrder.id)) return;
		Order.revert(activeOrder.id);
	}, [activeOrder?.id]);

	const sosMessageModel = useMemo(() => {
		const { models } = subscribeChatMessage;
		if (!models || !models?.length) {
			return [];
		}

		return models.filter(
			(item) =>
				item.priority === Priority.SOS &&
				item.id > LastIDReadInMessageSMS,
		);
	}, [LastIDReadInMessageSMS, subscribeChatMessage]);

	useLayoutEffect(() => {
		if (sosMessageModel.length) setShowSOSModal(true);
	}, [sosMessageModel]);

	const handleSOSModal = useCallback(() => {
		sosMessageModel.forEach((item) => {
			ChatMessage.readSosMessageByDispatcher(item.id);
		});
		setShowSOSModal(false);
	}, [sosMessageModel]);

	useEffect(() => {
		dispatch(initOrderCard());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const carClasses = useMemo(() => {
		if (
			!isSetOrderExecutorModalOpen &&
			!activeOrder?.id &&
			!activeOrder?.orderToCarClasses
		) {
			return [];
		}

		return activeOrder.orderToCarClasses.map(({ carClass }) => carClass);
	}, [
		activeOrder?.id,
		activeOrder?.orderToCarClasses,
		isSetOrderExecutorModalOpen,
	]);

	const services = useMemo(() => {
		if (
			!isSetOrderExecutorModalOpen &&
			!activeOrder?.id &&
			!activeOrder?.orderToServices
		) {
			return [];
		}

		return activeOrder.orderToServices?.map(({ service }) => service) || [];
	}, [
		activeOrder?.id,
		activeOrder?.orderToServices,
		isSetOrderExecutorModalOpen,
	]);

	const isShowMainOrderPage = useMemo(
		() =>
			!![
				TabAccessNames[TabKeys.LIVE],
				TabAccessNames[TabKeys.PRELIMINARY],
				TabAccessNames[TabKeys.EXECUTING],
				TabAccessNames[TabKeys.ALL],
				TabAccessNames[TabKeys.CLOSED],
			].filter((tab) => hasAccess(tab, personalRole)).length,
		[personalRole],
	);

	const warnBeforeCloseOrder = useMemo(() => {
		const pathT = "pages.mainPage.pages.orders.warn";
		const base = t(`${pathT}.base`) ?? "";
		const and = t(`${pathT}.and`) ?? "";
		const warnWhenClosingMeteredOrderT =
			t(`${pathT}.warnWhenClosingMeteredOrder`) ?? "";
		const warnWhenClosingNoDistanceOrderT =
			t(`${pathT}.warnWhenClosingNoDistanceOrder`) ?? "";
		const warnWhenClosingZeroCostOrderT =
			t(`${pathT}.warnWhenClosingZeroCostOrder`) ?? "";
		const warnWhenClosingOwnOrderT =
			t(`${pathT}.warnWhenClosingOwnOrder`) ?? "";

		const allWarn = warnBeforeCloseOrderType
			.map((num, i, arr) => {
				const next = arr?.[i + 1] ? and : "";
				let text = "";

				if (num === 1) text = warnWhenClosingNoDistanceOrderT;
				if (num === 2) text = warnWhenClosingMeteredOrderT;
				if (num === 3) text = warnWhenClosingZeroCostOrderT;
				if (num === 4) text = warnWhenClosingOwnOrderT;

				return `${text} ${text ? `${next}` : ""}`.trim();
			})
			.join(" ");

		const value = `${base} ${allWarn}`.trim();

		return `${value}?`;
	}, [t, warnBeforeCloseOrderType]);

	const warnBeforeCloseOrderWithTaximeter = useMemo(() => {
		const pathT = "pages.mainPage.pages.orders.warn";
		const base = t([`${pathT}.base`]) ?? "";
		const text = t(`${pathT}.warnWithTaximeter`) ?? "";

		const value = `${base} ${text}`.trim();
		return `${value}?`;
	}, [t]);

	const loaderTaximeter = useMemo(() => {
		const value =
			t("pages.mainPage.pages.orders.warn.loaderTaximeter") ?? "";
		return value;
	}, [t]);

	return (
		<>
			<StyledGrid
				areas=""
				columns={{ column: "auto" }}
				rows={{ row: "1fr", rows: "76px" }}
			>
				{modal.open && (
					<Suspense>
						<OrderModal
							activePoint={activePoint}
							setActivePoint={setActivePoint}
							close={closeCards}
							pointModal={pointModal}
							setPointModal={setPointModal}
						/>
					</Suspense>
				)}
				{canSubscribe && <OrderPageModelsLoader />}
				<PerfProfiler id="OrderHeader">
					<HeaderOrder
						onRevertOrderClick={onRevertOrder}
						onOpenEditOrderModal={() => {
							if (activeOrder?.id) {
								dispatch(openOrderCard(activeOrder.id));
							}
						}}
						toggleModalStatus={() => {
							if (isCallUs) {
								dispatch(
									orderPage.actions.setBufferOpenBaseThenCallContinue(
										true,
									),
								);
							}
							dispatch(openOrderCard());
						}}
						onCloseOrderClick={openCloseOrderModal}
						onCloneOrderClick={openCloneOrderModal}
						onSetOrderExecutorClick={openSetOrderExecutorModal}
						onApproveOrderClick={approveOrder}
						onRecalculateOrderClick={recalculateOrder}
						onRecalculateOrderWithActualRatesClick={
							recalculateOrderWithActualRates
						}
						onRefuseOrderClick={refuseOrder}
						onSendOrderToAirClick={sendOrderToAir}
						onSendOrderToExecuting={sendOrderToExecuting}
						onToggleOrderAirClick={toggleOrderAir}
						onSetCallToClientBeingRequiredClick={
							setCallToClientBeingRequired
						}
						onSetCallToClientSuccessClick={setCallToClientSuccess}
						onSetCallToExecutorBeingRequiredClick={
							setCallToExecutorBeingRequired
						}
						onSetCallToExecutorSuccessClick={
							setCallToExecutorSuccess
						}
						onShowClientNumberToExecutorClick={
							toggleShowClientPhoneToExecutor
						}
					/>
				</PerfProfiler>
				{isShowMainOrderPage && (
					<StyledMainPage>
						<PerfProfiler id="OrdersDock">
							<MainOrderPage />
						</PerfProfiler>
					</StyledMainPage>
				)}
				{isShowContextMenu && (
					<ContextMenu
						coordinate={contextMenuCoordinate}
						onClose={closeContextMenu}
						booferAnswerParentID={booferAnswerParentID}
					/>
				)}
				{isWarnBeforeCloseModalOpen && (
					<DeleteModal
						label={warnBeforeCloseOrder}
						onCancel={() => {
							setIsWarnBeforeCloseModalOpen(false);
							setWarnBeforeCloseOrderType([]);
						}}
						onConfirm={() => {
							setIsWarnBeforeCloseModalOpen(false);
							setIsCloseOrderModalOpen(true);
							setWarnBeforeCloseOrderType([]);
						}}
					/>
				)}
				{isLoaderTaximeter && (
					<DeleteModal
						h="0px"
						label={
							<SuspenseLoader
								loader="5"
								text={loaderTaximeter}
								bgC="transparent"
							/>
						}
						disabled
						isConfirm={false}
						isCancel={false}
						onCancel={() => {}}
						onConfirm={() => {}}
					/>
				)}
				{isWarnBeforeCloseOrderWithTaximeterModalOpen && (
					<DeleteModal
						label={warnBeforeCloseOrderWithTaximeter}
						onCancel={() => {
							setParamsOrderForClosed(null);
							setIdOrderForClosed(null);
							setBlackListForClosed(null);
							setIsWarnBeforeOrderWithTaximeterModalOpen(false);
						}}
						onConfirm={() => {
							cloneOrderModal({
								orderId: idOrderForClosed,
								params: paramsOrderForClosed,
								blackList: blackListForClosed,
							});
						}}
					/>
				)}
				{isCloseOrderModalOpen && activeOrder.id && (
					<CloseOrderModal
						onClose={closeCloseOrderModal}
						onSubmit={submitCloseOrderModal}
					/>
				)}
				{isClientCancelOrderModalOpen && activeOrder.id && (
					<ClientCancelOrderModal
						addClientToBlackList={
							closeOrderModalValue?.addClientToBlackList ?? false
						}
						onClose={closeClientCancelOrderModal}
						onSubmit={submitClientCancelOrderModal}
					/>
				)}
				{isAddClientToBlackListModalOpen && activeOrder.id && (
					<AddClientToBlacklistModal
						onClose={closeAddClientToBlacklistModal}
						onSubmit={submitAddClientToBlacklistModal}
					/>
				)}
				{isCloneOrderModalOpen && activeOrder.id && (
					<CopyOrderModal
						onClose={closeCloneOrderModal}
						onSubmit={cloneOrderModalOnSubmit}
					/>
				)}
				{isSetOrderExecutorModalOpen && activeOrder.id && (
					<SetOrderExecutorModalMemo
						orderTime={
							activeOrder?.isPreliminary
								? activeOrder?.orderDate
								: undefined
						}
						ordertaxiServiceId={activeOrder.taxiService?.id}
						onClose={closeSetOrderExecutorModal}
						onSubmit={setOrderExecutorModalOnSubmit}
						carClasses={carClasses}
						services={services}
					/>
				)}
				{isRefuseOrderModalOpen && (
					<DeleteModal
						label={t("pages.mainPage.pages.orders.str201") ?? ""}
						onCancel={closeRefuseOrderModal}
						onConfirm={confirmRefuseOrderModal}
					/>
				)}
			</StyledGrid>
			{showSOSModal && (
				<SOSModal
					value={{ messages: sosMessageModel }}
					onClose={handleSOSModal}
					onSave={handleSOSModal}
				/>
			)}
		</>
	);
};

export default memo(OrdersPage);
